import React, { useEffect, useRef, useState, useCallback } from 'react'
import ReactResponsiveModal from 'react-responsive-modal'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { BiTrash, BiDownload, BiCamera } from 'react-icons/bi'
import { Input, Select } from '../../components/ModalInputs'
import ModalFooter from '../../components/ModalFooter/ModalFooter'
import Confirmation from '../../components/Confirmation/Confirmation'
import {
  getEquipmentCard,
  clearEquipmentCard,
  clearEquipmentFiles,
  createEquipment,
  updateEquipment,
  getEquipmentLookups,
  getEquipmentPhoto,
  uploadEquipmentPhoto,
  deleteEquipmentPhoto,
  getEquipmentFiles,
  uploadEquipmentFiles,
  downloadEquipmentFile,
  deleteEquipmentFile,
} from '../../actions/equipment'
import styles from './styles.module.css'

const validationSchema = Yup.object({
  equipname: Yup.string().trim().required('Поле «Название» обязательно для заполнения.').max(300, 'Должно быть не более 300 символов'),
})

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} Б`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`
}

const fileExt = (name) => {
  const ext = name.split('.').pop()
  return (ext && ext !== name) ? ext.slice(0, 4) : 'файл'
}

export default function EquipmentModal({ data, closeModal, onSaved }) {
  const dispatch = useDispatch()
  const { card, lookups, files } = useSelector(state => state.equipment)

  const addMode = !data?.idequipment
  const id = data?.idequipment

  const [confirmClose, setConfirmClose] = useState(false)
  const [saving, setSaving] = useState(false)
  const [photoUrl, setPhotoUrl] = useState(null)
  const photoInputRef = useRef(null)
  const filesInputRef = useRef(null)

  const acceptConfirmation = useCallback(() => { closeModal(); setConfirmClose(false) }, [closeModal])
  const rejectConfirmation = useCallback(() => setConfirmClose(false), [])

  const setPhotoFromBlob = (blob) => {
    setPhotoUrl(prev => {
      if (prev) URL.revokeObjectURL(prev)
      return blob ? URL.createObjectURL(blob) : null
    })
  }

  const loadPhoto = async () => {
    const blob = await dispatch(getEquipmentPhoto(id))
    setPhotoFromBlob(blob)
  }

  useEffect(() => {
    const load = async () => {
      if (addMode) {
        dispatch(getEquipmentLookups())
        return
      }
      const cardData = await dispatch(getEquipmentCard(id))
      dispatch(getEquipmentLookups(cardData?.mid))
      dispatch(getEquipmentFiles(id))
      if (cardData?.has_photo) loadPhoto()
    }
    load()
    return () => {
      dispatch(clearEquipmentCard())
      dispatch(clearEquipmentFiles())
      setPhotoFromBlob(null)
    }
  }, []) // eslint-disable-line

  const source = addMode ? null : card

  const toPayload = (values) => {
    const text = (value) => value.trim() || null
    return {
      inventnumber: text(values.inventnumber),
      serialnumber: text(values.serialnumber),
      equipname: values.equipname.trim(),
      edizm: text(values.edizm),
      quantity: values.quantity !== '' ? Number(values.quantity) : null,
      price: values.price !== '' ? Number(String(values.price).replace(',', '.')) : null,
      idequipgroup: values.idequipgroup ? Number(values.idequipgroup) : null,
      equiptype: text(values.equiptype),
      status: text(values.status),
      manufacturer: text(values.manufacturer),
      autor: text(values.autor),
      mid: values.mid ? Number(values.mid) : null,
      incomdate: values.incomdate || null,
      incomnum: text(values.incomnum),
      outcomdate: values.outcomdate || null,
      outcomnum: text(values.outcomnum),
      description: text(values.description),
      rid: values.rid ? Number(values.rid) : null,
    }
  }

  const handleSave = async (values) => {
    setSaving(true)
    const payload = toPayload(values)
    const result = addMode
      ? await dispatch(createEquipment(payload))
      : await dispatch(updateEquipment(id, payload))
    setSaving(false)
    if (result) onSaved()
  }

  const {
    values,
    errors,
    dirty,
    submitCount,
    setFieldValue,
    handleChange,
    handleSubmit
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      inventnumber: source?.inventnumber ?? '',
      serialnumber: source?.serialnumber ?? '',
      equipname: source?.equipname ?? '',
      edizm: source?.edizm ?? '',
      quantity: source?.quantity ?? '',
      price: source?.price ?? '',
      idequipgroup: source?.idequipgroup ?? '',
      equiptype: source?.equiptype ?? '',
      status: source?.status ?? '',
      manufacturer: source?.manufacturer ?? '',
      autor: source?.autor ?? '',
      mid: source?.mid ?? '',
      incomdate: source?.incomdate ?? '',
      incomnum: source?.incomnum ?? '',
      outcomdate: source?.outcomdate ?? '',
      outcomnum: source?.outcomnum ?? '',
      description: source?.description ?? '',
      rid: source?.rid ?? '',
    },
    validationSchema,
    onSubmit: handleSave
  })

  const handleClose = useCallback(() => {
    if (dirty) setConfirmClose(true)
    else closeModal()
  }, [dirty, closeModal])

  // ошибки показываем только после «Сохранить», при вводе значения — скрываются
  const error = (fieldName) => submitCount > 0 && errors[fieldName]

  const handleQuantityChange = (e) => setFieldValue('quantity', e.target.value.replace(/\D/g, ''))

  const handlePriceChange = (e) => setFieldValue('price', e.target.value.replace(/[^\d.,]/g, ''))

  // фото и файлы привязываются к id — доступны только для сохранённой записи
  const handlePhotoPick = async (e) => {
    const file = e.target.files[0]
    e.target.value = ''
    if (!file) return
    const success = await dispatch(uploadEquipmentPhoto(id, file))
    if (success) loadPhoto()
  }

  const handlePhotoDelete = async () => {
    const success = await dispatch(deleteEquipmentPhoto(id))
    if (success) setPhotoFromBlob(null)
  }

  const handleFilesPick = (e) => {
    const picked = Array.from(e.target.files)
    e.target.value = ''
    if (picked.length) dispatch(uploadEquipmentFiles(id, picked))
  }

  const handleFileDownload = async (fileName) => {
    const blob = await dispatch(downloadEquipmentFile(id, fileName))
    if (!blob) return
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleFileDelete = (fileName) => dispatch(deleteEquipmentFile(id, fileName))

  return (
    <ReactResponsiveModal
      open={!!data}
      onClose={handleClose}
      showCloseIcon={false}
      closeOnOverlayClick={true}
      closeOnEsc={true}
      center
      classNames={{
        modal: styles.modal,
        modalContainer: styles.modalContainer
      }}
    >
      <>
        <div className={styles.modalMain}>
          <h2>{addMode ? 'Добавление записи' : 'Редактирование записи'}</h2>

          <div className={styles.formGrid}>
            <div className={styles.formLeft}>
              <div className={styles.inputsRow}>
                <Input
                  label='Инвентарный номер'
                  name='inventnumber'
                  value={values.inventnumber}
                  onChange={handleChange}
                  maxLength='50'
                  autoComplete='off'
                  autoFocus
                />
                <Input
                  label='Серийный номер'
                  name='serialnumber'
                  value={values.serialnumber}
                  onChange={handleChange}
                  maxLength='100'
                  autoComplete='off'
                />
              </div>
              <Input
                label='Название'
                name='equipname'
                value={values.equipname}
                error={error('equipname')}
                onChange={handleChange}
                maxLength='300'
                autoComplete='off'
                required
              />
              <div className={styles.inputsRow3}>
                <Input
                  label='Ед. измерения'
                  name='edizm'
                  value={values.edizm}
                  onChange={handleChange}
                  maxLength='50'
                  autoComplete='off'
                  list='equipEdizmOptions'
                />
                <Input
                  label='Кол-во'
                  name='quantity'
                  value={values.quantity}
                  onChange={handleQuantityChange}
                  inputMode='numeric'
                  maxLength='9'
                  autoComplete='off'
                />
                <Input
                  label='Цена'
                  name='price'
                  value={values.price}
                  onChange={handlePriceChange}
                  inputMode='decimal'
                  maxLength='15'
                  autoComplete='off'
                />
              </div>
              <Select
                label='Группа оборудования'
                name='idequipgroup'
                value={values.idequipgroup}
                onChange={handleChange}
              >
                <option value=''>—</option>
                {lookups.groups?.map(item =>
                  <option key={item.idequipgroup} value={item.idequipgroup}>{item.groupname}</option>
                )}
              </Select>
              <div className={styles.inputsRow}>
                <Input
                  label='Тип'
                  name='equiptype'
                  value={values.equiptype}
                  onChange={handleChange}
                  maxLength='50'
                  autoComplete='off'
                  list='equipTypeOptions'
                />
                <Input
                  label='Состояние'
                  name='status'
                  value={values.status}
                  onChange={handleChange}
                  maxLength='50'
                  autoComplete='off'
                  list='equipStatusOptions'
                />
              </div>
              <div className={styles.inputsRow}>
                <Input
                  label='Изготовитель/издатель'
                  name='manufacturer'
                  value={values.manufacturer}
                  onChange={handleChange}
                  maxLength='40'
                  autoComplete='off'
                />
                <Input
                  label='Автор/авторы'
                  name='autor'
                  value={values.autor}
                  onChange={handleChange}
                  maxLength='40'
                  autoComplete='off'
                />
              </div>
              <Select
                label='Местонахождение'
                name='rid'
                value={values.rid}
                onChange={handleChange}
              >
                <option value=''>—</option>
                {lookups.rooms?.map(item =>
                  <option key={item.rid} value={item.rid}>{item.name}</option>
                )}
              </Select>
              <Select
                label='Ответственный'
                name='mid'
                value={values.mid}
                onChange={handleChange}
              >
                <option value=''>—</option>
                {lookups.responsible?.map(item =>
                  <option key={item.mid} value={item.mid}>{item.fio}</option>
                )}
              </Select>
              <div className={styles.inputsRow}>
                <Input
                  label='Дата оприходования'
                  type='date'
                  name='incomdate'
                  value={values.incomdate}
                  onChange={handleChange}
                />
                <Input
                  label='Номер оприходования'
                  name='incomnum'
                  value={values.incomnum}
                  onChange={handleChange}
                  maxLength='15'
                  autoComplete='off'
                />
              </div>
              <div className={styles.inputsRow}>
                <Input
                  label='Дата выбытия'
                  type='date'
                  name='outcomdate'
                  value={values.outcomdate}
                  onChange={handleChange}
                />
                <Input
                  label='Номер выбытия'
                  name='outcomnum'
                  value={values.outcomnum}
                  onChange={handleChange}
                  maxLength='15'
                  autoComplete='off'
                />
              </div>
              <Input
                label='Примечание'
                name='description'
                value={values.description}
                onChange={handleChange}
                maxLength='100'
                autoComplete='off'
              />

              <datalist id='equipEdizmOptions'>
                {lookups.edizm?.map(value => <option key={value} value={value} />)}
              </datalist>
              <datalist id='equipTypeOptions'>
                {lookups.equiptype?.map(value => <option key={value} value={value} />)}
              </datalist>
              <datalist id='equipStatusOptions'>
                {lookups.status?.map(value => <option key={value} value={value} />)}
              </datalist>
            </div>

            <div className={styles.formRight}>
              <p className={styles.sideLabel}>Фотография</p>
              {addMode
                ? <p className={styles.sideHint}>Фото можно добавить после сохранения записи</p>
                : <>
                  <input
                    ref={photoInputRef}
                    type='file'
                    accept='image/jpeg,image/png'
                    hidden
                    onChange={handlePhotoPick}
                  />
                  {photoUrl
                    ? <div className={styles.photoFilled}>
                      <img src={photoUrl} alt='Фото оборудования' />
                      <div className={styles.photoActions}>
                        <button type='button' className={styles.photoAction} onClick={() => photoInputRef.current.click()}>
                          <BiCamera size={16} />
                          Заменить
                        </button>
                        <button type='button' className={`${styles.photoAction} ${styles.photoActionDanger}`} onClick={handlePhotoDelete}>
                          <BiTrash size={16} />
                          Удалить
                        </button>
                      </div>
                    </div>
                    : <button type='button' className={styles.photoEmpty} onClick={() => photoInputRef.current.click()}>
                      <BiCamera size={34} />
                      <span>Загрузить фото</span>
                      <small>JPG, PNG</small>
                    </button>
                  }
                </>
              }

              <p className={styles.sideLabel}>Файлы</p>
              {addMode
                ? <p className={styles.sideHint}>Файлы можно добавить после сохранения записи</p>
                : <>
                  <input
                    ref={filesInputRef}
                    type='file'
                    multiple
                    hidden
                    onChange={handleFilesPick}
                  />
                  <button type='button' className={styles.filesDrop} onClick={() => filesInputRef.current.click()}>
                    <BiDownload size={18} style={{ transform: 'rotate(180deg)' }} />
                    Добавить файл
                  </button>
                  {files?.length
                    ? <div className={styles.filesList}>
                      {files.map(file =>
                        <div key={file.file_name} className={styles.fileItem}>
                          <div className={styles.fileIcon}>{fileExt(file.file_name)}</div>
                          <div className={styles.fileMeta}>
                            <div className={styles.fileName} title={file.file_name}>{file.file_name}</div>
                            <div className={styles.fileSize}>{formatFileSize(file.file_size)}</div>
                          </div>
                          <button
                            type='button'
                            className={styles.fileBtn}
                            onClick={() => handleFileDownload(file.file_name)}
                            title='Скачать'
                            aria-label='Скачать файл'
                          >
                            <BiDownload size={18} />
                          </button>
                          <button
                            type='button'
                            className={`${styles.fileBtn} ${styles.fileBtnDanger}`}
                            onClick={() => handleFileDelete(file.file_name)}
                            title='Удалить'
                            aria-label='Удалить файл'
                          >
                            <BiTrash size={18} />
                          </button>
                        </div>
                      )}
                    </div>
                    : <p className={styles.filesEmpty}>Файлов пока нет</p>
                  }
                </>
              }
            </div>
          </div>
        </div>

        <ModalFooter handleSubmit={handleSubmit} closeModal={closeModal} loading={saving} />
        {confirmClose && (
          <Confirmation
            title="Закрыть без сохранения изменений?"
            acceptConfirmation={acceptConfirmation}
            rejectConfirmation={rejectConfirmation}
          />
        )}
      </>
    </ReactResponsiveModal>
  )
}
